import { Caption1, Card, CardHeader, Divider, Link, makeStyles, shorthands, Subtitle1, Subtitle2, Title1 } from '@fluentui/react-components';
import { AddCircleFilled, Clover48Regular } from '@fluentui/react-icons';
import { t } from 'i18next';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfirm } from '../../Contexts/hooks';
import { useNotification } from '../../Contexts/NotificationContext';
import { GptStatus, IGptConfiguration } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { buildRoute, ExploreGptRoute, PersonalGptRoute, PersonalGptSpecificRoute } from '../../Routes';
import { setConversationsAction } from '../../store/actions/conversationActions';
import { deleteGptConfiguration } from '../../store/actions/gptConfigurationsAction';
import { useFeature, useRootState } from '../../store/hooks';
import { getGptIconAsset } from '../../Utils/image';
import GptMessageBar from '../Common/GptMessageBar';
import { CDialog } from '../Dialogs/CDialog';
import { ExploreGptSection } from './ExploreGptSection';
import { getInitialConversationPageSize } from '../../Hooks/useInfiniteConversations';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.flex(1, 1, 'auto'),
        ...shorthands.padding('30px', '80px'),
        ...shorthands.gap('20px'),
        '@media (max-width: 768px)': {
            ...shorthands.padding('20px', '16px'),
        },
    },
    titleContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        ...shorthands.gap('10px'),
    },
    sectionsContainer: {
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('100px'),
        ...shorthands.margin('2rem', '0', '0', '0'),
        '@media (max-width: 768px)': {
            ...shorthands.gap('60px'),
            ...shorthands.margin('1.5rem', '0', '0', '0'),
        },
    },
    dialogCard: {
        width: '400px',
    },
    dialogHeaderImage: {
        ...shorthands.borderRadius('4px'),
        height: '50px',
    },
});

export interface ExploreGptProps {
    gpts: IGptConfiguration[];
    onGptAdd: (gpt: IGptConfiguration) => void;
    onGptRemove: (gpt: IGptConfiguration) => void;
}

export const ExploreGptView: FC<ExploreGptProps> = ({ gpts, onGptAdd, onGptRemove }: ExploreGptProps) => {
    const styles = useStyles();
    const chatFeature = useFeature('chat');
    const isPersonalGptEnabled = chatFeature.personalGpt?.isEnabled;
    const [featuredGpts, setFeaturedGpts] = useState<IGptConfiguration[]>([]);
    const [personalGpts, setPersonalGpts] = useState<IGptConfiguration[]>([]);
    const [selectedGpt, setSelectedGpt] = useState<SelectedGpt | undefined>(undefined);
    const confirm = useConfirm();
    const navigate = useNavigate();
    const { dispatchNotification } = useNotification();
    const { dispatch } = useRootState();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedAction, setSelectedAction] = useState<string | undefined>(undefined);

    useEffect(() => {
        setFeaturedGpts(gpts.filter((gpt) => !gpt.isDefault && !gpt.isPersonal));
        setPersonalGpts(gpts.filter((gpt) => gpt.isPersonal));
    }, [gpts]);

    const onCancel = () => {
        setSelectedGpt(undefined);
    };

    const onCreatePersonalGpt = useCallback(() => {
        if (personalGpts.length < chatFeature.personalGpt!.maxNumberOfGpts!) {
            navigate(PersonalGptRoute);
        } else {
            confirm({
                show: true,
                title: t(`application.dialog.warningTitle`),
                message: t('exploreGpt.personalGpt.warning.maxGptReachedMessage'),
                confirmText: t('Ok'),
            });
        }
    }, [personalGpts]);

    const onPrimaryAction = useCallback(() => {
        if (selectedGpt) {
            if (!selectedGpt.config.isDefault && selectedGpt.isPinned) {
                onGptRemove(selectedGpt.config);
            } else {
                onGptAdd(selectedGpt.config);
            }
            setSelectedGpt(undefined);
        }
    }, [selectedGpt]);

    const onPersonalGptModify = useCallback(() => {
        if (selectedGpt?.config.id) {
            navigate(buildRoute(PersonalGptSpecificRoute, { id: selectedGpt.config.id }));
        }
        setSelectedGpt(undefined);
    }, [selectedGpt]);

    const handleDeletion = useCallback(() => {
        confirm({
            message: t('exploreGpt.confirmDeleteMessage'),
            show: true,
            title: t('exploreGpt.confirmDeleteTitle'),
            cancelText: t('No'),
            confirmText: t('Yes'),
        }).then(() => {
            performEnterpriseGPTApiCall(async (client, abortSignal) => {
                try {
                    setIsLoading(true);
                    setSelectedAction('delete');
                    await client.deletePersonalGpt(selectedGpt!.config.id!);
                    console.log('Personal GPT deleted:', selectedGpt!.config.id!);
                    // update the list of conversations after deleting all the ones related to the deleted personal GPT
                    const conversationsPage = await client.getConversationsPaged(
                        undefined, // projectId = null
                        undefined, // no cursor (first page)
                        getInitialConversationPageSize(),        // load first X conversations
                        false,     // fullLoad = false
                        abortSignal
                    );
                    const conversations = conversationsPage.conversations ?? [];
                    const updatedConversations = conversations.map((c) => ({ ...c, active: false, draft: false, prompts: c.prompts?.map((p) => ({ ...p, aborted: p.answer === null })) }));
                    dispatch(setConversationsAction(updatedConversations));
                    setIsLoading(false);
                    dispatch(deleteGptConfiguration(selectedGpt!.config.id!));
                    navigate(ExploreGptRoute);
                    dispatchNotification('personalGpt.actions.delete', 'success');
                    setSelectedGpt(undefined);
                } catch (e) {
                    setIsLoading(false);
                    dispatchNotification('error.api.delete.personalGpt', 'error');
                }
            });
        });
    }, [selectedGpt, confirm, dispatch, navigate, dispatchNotification]);

    return (
        <>
            <div className={styles.container}>
                <div className={styles.titleContainer}>
                    <Clover48Regular />
                    <Title1>{t('exploreGpt.title')}</Title1>
                </div>
                <Subtitle1>{t('exploreGpt.subtitle')}</Subtitle1>
                <div className={styles.sectionsContainer}>
                    <ExploreGptSection
                        title={t('exploreGpt.featuredSectionTitle')}
                        gpts={featuredGpts}
                        onGptSelected={(gptConfiguration, isPinned) => setSelectedGpt({ config: gptConfiguration, isPinned })}
                        lastCardDescription='Stay tuned, more SyGPT Tools coming soon!'
                    />
                    {isPersonalGptEnabled && (
                        <ExploreGptSection
                            title={t('exploreGpt.personalGpt.sectionTitle')}
                            infoLabel={
                                <>
                                    Start to create your own GPT now.{' '}
                                    <Link href='https://syensqo.com' target='_blank'>
                                        Learn more
                                    </Link>
                                </>
                            }
                            gpts={personalGpts}
                            onGptSelected={(gptConfiguration, isPinned) => setSelectedGpt({ config: gptConfiguration, isPinned })}
                            firstCardTitle={t('exploreGpt.personalGpt.createCardTitle')}
                            firstCardIcon={<AddCircleFilled />}
                            firstCardAction={onCreatePersonalGpt}
                        />
                    )}
                </div>
            </div>

            {selectedGpt && (
                <CDialog
                    open={!!selectedGpt}
                    title=''
                    onPrimaryAction={() => onPrimaryAction()}
                    primaryActionText={!selectedGpt.config.isDefault && selectedGpt.isPinned ? t('exploreGpt.removeGPT') : t('exploreGpt.startChat')}
                    onSecondaryAction={onCancel}
                    secondaryActionText={t('exploreGpt.cancel')}
                    additionalActions={selectedGpt.config.isPersonal ? [
                        { text: t('exploreGpt.delete'), onAction: handleDeletion },
                        { text: t('exploreGpt.edit'), onAction: onPersonalGptModify },
                    ] : []}
                    disablePrimaryAction={selectedGpt.config.isPersonal && selectedGpt.config.status !== GptStatus.Ready}
                >
                    <Card appearance='subtle'>
                        <CardHeader
                            image={<img className={styles.dialogHeaderImage} src={getGptIconAsset(selectedGpt.config.icon)} />}
                            header={<Subtitle2>{selectedGpt.config.name}</Subtitle2>}
                            description={<Caption1>{selectedGpt.config.isPersonal ? 'Created by you' : 'By Syensqo'}</Caption1>}
                        />
                        <p>{selectedGpt.config.description}</p>
                        <Divider />
                        {selectedGpt.config.disclaimer && <GptMessageBar title='Note' text={selectedGpt.config.disclaimer!} />}
                    </Card>
                </CDialog>
            )}
        </>
    );
};

interface SelectedGpt {
    config: IGptConfiguration;
    isPinned: boolean;
}
